import {useEffect, useMemo, useRef, useState} from 'react';
import {Outlet, useLocation} from "react-router-dom";
import {useMarketingDecisionReplay} from "marketing/useMarketingDecisionReplay";
import {useAdvertisingConsentReplay} from "consent/useAdvertisingConsentReplay";
import NavBar from "./nav/normal/NavBar";
import {SiteFooter} from "./nav/SiteFooter";
import {showsSiteFooter} from "./nav/siteFooterRoutes";
import "./utils/AxiosUtils"; // installs the (firebase-free) auth axios interceptors eagerly
import {useAuthenticatedSession, useIsLoggedIn, useUser} from "./UserProvider";
import {AuthenticatedSessionState} from "./auth/AuthenticatedSessionWaiter";
import {Toast, ToastContext, ToastVariants} from "./Toast";
import Banners from "./banner/Banners";
import {AppRoot, Content, Main} from "./AppComponents";
import {usePrefetchPrivateRoutes} from "./nav/usePrefetchPrivateRoutes";
import {usePrefetchPrivateData} from "./nav/usePrefetchPrivateData";
import {useMessages} from "./i18n";
import {type FeatureSheetId, featureSheetIdFor, isHomeRoute} from "./featurePanelRoutes";
import {transfersTabFor} from "./transfers/transfersTabs";
import {isPersonalizationRoute} from "./account/brand/personalizationRoute";
import {FeatureCanvas, FeaturePanel} from "./components/FeaturePanel";
import {useCommandPalette} from "./commands";
import {useReferralAttribution} from "./referral/useReferralAttribution";
import {ReferralLandingPicker} from "./referral/ReferralLandingPicker";
import {PromoLinkDialog} from "./offers/promo/PromoLinkDialog";
import {useAdAttribution} from "./attribution/useAdAttribution";
import {useMetaPixel} from "./analytics/useMetaPixel";
import {useRedditPixel} from "./analytics/useRedditPixel";
import {usePageViewReporting} from "./analytics/usePageViewReporting";
import {usePurchaseConversions} from "./analytics/usePurchaseConversions";
import {useTermsAcceptanceReplay} from "./terms/useTermsAcceptanceReplay";
import {useEmailVerificationPropagation} from "./emailVerification/useEmailVerificationPropagation";
import {offerEvents} from "./upsell/offerEvents";
import {wireOfferTelemetry} from "./upsell/offerEventBeacon";
import {recipientFunnelEvents} from "./download/recipient/recipientFunnelEvents";
import {recipientArrival} from "./download/recipient/recipientArrival";
import {wireRecipientFunnelTelemetry} from "./download/recipient/recipientFunnelBeacon";
import {sendEvents} from "./home/sendEvents";
import {browserSignupChannel, registerRecipientPageArm} from "./attribution/signupChannelHeader";
import {transferGamesArm} from "./games/gameAdapters";
import {useIsomorphicLayoutEffect} from "./utils/useIsomorphicLayoutEffect";
import {DownloadReplyProvider, useDownloadReply} from "download/reply/DownloadReplyProvider";
import {useKeptUploadsSweep} from "home/resume/useKeptUploadsSweep";

/*
 * A download page's signup channel names its transfer-games group (kitecorp/transferify-ui#2889), so the
 * recipient arcade's test can split signups by group. Registered here, at module scope, because the header
 * is derived on the page load's first api request, which no effect is guaranteed to precede. It only hands
 * over the function: the group is resolved the first time a download-route landing asks for it.
 */
registerRecipientPageArm(transferGamesArm);

/**
 * App shell / root layout.
 *
 * Renders the persistent chrome (navigation, banners, toasts) around the active route, which is
 * injected through react-router's {@link Outlet}. The ToS consent gate left this shell in ui#1170 —
 * it is the drop area's first content now, owned by `home/CreateFlow`. The route table itself lives in
 * `routes.tsx` so the same declarative routes can be consumed both by the client SPA and by
 * `vite-react-ssg` for static pre-rendering of the public pages.
 */
function App() {
	let [user, setUser] = useUser();
	let [, setAuthenticatedSession] = useAuthenticatedSession();
	// The account as it stands at EMIT time, for the offer beacon's account check below: the subscription
	// is wired once on mount, so a closure over `user` would answer with the visitor who existed then.
	const userRef = useRef(user);
	userRef.current = user;

	// The message a toast shows, or null when it's closed. Typed explicitly (not inferred `null`) so the
	// setters satisfy the `ToastApi` string slot the context now enforces (ui#361).
	const [showErrorMessageToast, setShowErrorMessageToast] = useState<string | null>(null);
	const [showWarningMessageToast, setShowWarningMessageToast] = useState<string | null>(null);
	const [showInfoMessageToast, setShowInfoMessageToast] = useState<string | null>(null);
	const [showSuccessMessageToast, setShowSuccessMessageToast] = useState<string | null>(null);

	// Track the Firebase auth state (anonymous on first load; real account on sign-in). The session
	// bootstrap — and the whole @firebase/auth SDK it pulls — is DYNAMICALLY imported here (perf
	// #153), so Firebase stays out of the eager bundle and loads just after hydration, warming the
	// shared auth chunk the axios interceptor and the login modal reuse. Browser-only: effects never
	// run during SSG, so the static HTML is still produced in the logged-out state.
	useEffect(() => {
		let unsubscribe = () => {};
		let cancelled = false;
		import("./auth/firebaseSession").then(({bootstrapSession}) => {
			if (!cancelled) {
				unsubscribe = bootstrapSession(setUser, setAuthenticatedSession);
			}
		}, (error: unknown) => {
			// The session chunk did not load (offline, a deploy that replaced it mid-visit). Nothing would
			// ever publish a terminal state for this tab, and every boundary that waits on the session
			// with no timer (a Send pressed by a cached sign-in, ui#2088; a private route, ui#2210) would
			// hold for the life of the tab. Signed out is the one answer a missing bootstrap can honestly
			// give: it withholds rather than grants, the cached user stays on screen exactly as before, and
			// the next page load asks again.
			console.error("Loading the session bootstrap failed; publishing the session as signed out", error);
			if (!cancelled) {
				setAuthenticatedSession(AuthenticatedSessionState.FailedOrAnonymous);
			}
		});
		return () => {
			cancelled = true;
			unsubscribe();
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Warm the private-route chunks once, in the background, after login (#146) — instant feature opens.
	usePrefetchPrivateRoutes();
	// Warm the private-destination list DATA once after login (ui#284) — so the FIRST visit paints its
	// rows instantly instead of a cold-load skeleton (the chunk warm-up above only covers the code).
	usePrefetchPrivateData();

	// Referral attribution (kitecorp/transferify-api-service#433). Mounted on the SHELL, not on the landing
	// page: a share link can point at any public route (pricing, a download, a shared transfer), and a
	// capture that only ran on "/" would silently lose most links. Both effects are no-ops without a stored
	// or query-string code, so nothing runs for the overwhelming majority of visits.
	// Its OUTCOME is read since ui#2020: the landing picker promises a visitor a gift before they have
	// an account, so it has to know whether the api applied it to the account they went on to create.
	const referralAttribution = useReferralAttribution();

	// Advertising attribution (ui#256), mounted on the shell for the same reason and gated by consent per
	// purpose: nothing is STORED before a purpose is granted, and a click identifier's VALUE is never read
	// until advertising consent exists. The bare PRESENCE of an ad-click parameter is the one exception
	// (api#753, owner ruling 2026-08-20) — a boolean that singles nobody out, used consent-free so the
	// signup channel mix stops being biased by exactly the consent rate.
	useAdAttribution();

	// Page views (kitecorp/transferify-api-service#1929), on the shell because gtag used to send them for
	// EVERY route and nothing does now that the GA4 tag is gone. Above the pixel rather than below it:
	// the two are unrelated, and the pixel's own ordering contract is with `usePurchaseConversions`.
	usePageViewReporting();

	// The Reddit pixel (ui#834), mounted here rather than beside `useAnalyticsConsent` in RootLayout
	// because it needs the session for its match keys and the router for route changes, and RootLayout
	// sits outside both. Loads nothing at all until advertising consent is granted.
	useRedditPixel();
	// The Meta pixel (kitecorp/transferify#375), for the same two reasons, beside its sibling. Loads
	// nothing until advertising consent is granted against wording that names Meta, on a Meta-enabled site.
	useMetaPixel();
	// The browser half of a purchase conversion (api#1241), mounted RIGHT AFTER the pixels because it asks
	// whether a pixel is running and effects run in declaration order. On the shell rather than on the
	// three success surfaces, because a hosted subscription checkout returns to `/activate/<token>` —
	// outside the pixels' route scope — so the event can only be sent on the page the buyer continues to.
	usePurchaseConversions();

	// Terms acceptance (api#744), mounted on the shell because sign-in can complete on any route and the
	// click can happen before or after it. The adapter follows the cached account candidate and same-tab store
	// changes; the replay funnel verifies Firebase before assigning that account any unowned click.
	useTermsAcceptanceReplay();
	// Same gap, same shape, ONE mount (ui#2331): the signup-time marketing objection and the
	// first-page email grant are written once their account exists — sequentially, through a single
	// coordinator, because two independent replays raced the unconditional objection PUT against the
	// grant's revision-CAS window.
	useMarketingDecisionReplay();

	// The same gap for the ADVERTISING decision: it is reported when it is made, so a decision taken
	// while anonymous, or before the account-level record existed, never reached the api. The api now
	// enrols an account in Customer Match off that record, so the answer has to arrive; it collapses a
	// restatement of what it already holds, which is why this may run on every sign-in.
	useAdvertisingConsentReplay();

	// Email-verification propagation, mounted on the shell for two reasons: the api's marked continue
	// URL is a landing this app does not route, and the verify-email callout has two mount points that
	// are alive at once — hanging the watch off the callout would force two token refreshes for one
	// verification. Idle unless the account is signed in and its address unproven.
	useEmailVerificationPropagation();

	// The upsell funnel's ONE subscriber (api#999). Mounted on the shell for the same reason the two
	// attributions above are: the offer moments are spread across the success card, the files panel's
	// callouts, the download-usage banner and a purged row on the transfers page, so every narrower host
	// unmounts partway through the funnel it is supposed to be recording.
	//
	// This is the whole of the wiring between the surfaces and the api. Nothing that decides WHETHER to
	// make an offer knows it exists — owner's 2026-08-27 rule, swept by
	// `scripts/offer-telemetry-separation-sweep.mjs`.
	//
	// Since ui#1965 the same subscription carries the five paid-moment surfaces too, and it is handed
	// the account fact: the endpoint is authenticated, so an emit with nobody signed in is dropped here
	// rather than posted for a 401 (see `wireOfferTelemetry`).
	//
	// A LAYOUT effect, deliberately. React runs every layout effect in the tree before any passive one,
	// and one paid surface can be visible on the very first commit: a direct load of `/pricing` mounts
	// the plan picker inside this shell's Outlet, whose passive `usePaidMoment` effect would otherwise
	// emit the showing before this subscription existed — to nobody, with a latch that then stays armed
	// for the whole visit. The isomorphic form keeps the SSG render free of the layout-effect warning.
	useIsomorphicLayoutEffect(() => wireOfferTelemetry(offerEvents, () => userRef.current.isLoggedIn), []);

	// The recipient acquisition funnel's ONE subscriber (ui#1973). On the shell for a stronger reason than
	// the offer funnel's: the chip lives on the download page and the send it is trying to produce happens
	// in the composer, and no component is mounted for both. This is also where the document's arrival is
	// recognised — the claim link opens a NEW TAB, so the document that goes on to send never mounts a
	// download page at all and could not mark itself from one.
	useEffect(() => wireRecipientFunnelTelemetry({
		funnel: recipientFunnelEvents,
		sends: sendEvents,
		arrival: recipientArrival,
		channel: browserSignupChannel,
	}), []);

	// Hydration-safe (ui#74): false on the server AND first client render (matching the SSG
	// logged-out HTML), so the persistent nav + logo never mismatch and get discarded on refresh.
	let loggedIn = useIsLoggedIn();
	const {pathname} = useLocation();
	const reply = useDownloadReply();
	const m = useMessages();
	// ui#150/ui#1061: private destinations render inside the docked FeaturePanel only after auth. The
	// HomePage itself is App-owned on both pre-rendered home paths and remains at that SAME React position
	// when one of those destinations opens, so its CreateFlow draft survives the route boundary. This does
	// not repeat the reverted 72598f7 crash: no hydrated node is moved between owners; server and first
	// client render use the same App-owned tree.
	const sheetId = featureSheetIdFor(pathname);
	const onFeaturePanel = sheetId !== null && loggedIn;
	// ui#1292: the top bar's Personalization sheet owns a route now, and it is the second surface that
	// hangs a card over the persistent canvas. It is NOT a FeaturePanel route — the sheet is chrome
	// mounted by NavBar (see `account/brand/personalizationRoute`) — so it needs saying here separately,
	// and saying it is what keeps the sender's CreateFlow draft alive across the route boundary. Without
	// it the canvas would unmount on the way in and the sheet would open over a blank page.
	const onPersonalization = isPersonalizationRoute(pathname) && loggedIn;
	// It RENDERS the canvas but does NOT cover it, and the asymmetry is deliberate. Rendering is what
	// keeps the sender's CreateFlow draft alive across the route boundary; covering would change the
	// sender's drop surface, which this ticket did not ask for and which would contradict the decision
	// it rests on. Personalization owns a URL now, but it is still a TRANSIENT EDITOR rather than a
	// destination — and the sender's other transient editors, Options and Personalise, leave window-wide
	// drops live while they are open. `covered` therefore stays exactly what it was: the destinations.
	const showHomeCanvas = isHomeRoute(pathname) || onFeaturePanel || onPersonalization;
	// The composer lives on the canvas below. On a page without it, the unfinished uploads this browser kept that
	// are past their deadline are ended here instead, so they go wherever Transferify opens (ui#3204).
	useKeptUploadsSweep(showHomeCanvas || reply.available);
	// Localized sheet header — the visible headline AND the region's accessible name — reusing each
	// feature's existing copy. Which sheet a route opens is decided by `featureSheetIdFor` (react-free,
	// epic #26); this record only LOOKS UP copy for that identity, so there is no route rule here and no
	// untranslated string. Total over `FeatureSheetId`, so a new feature route cannot compile until it
	// has a name. Destination bodies own their feature content; the shared CardSheet owns the title,
	// localized accent and lede (ui#1058/ui#1061).
	const sheetHeaders: Record<FeatureSheetId, {title: string; titleAccent?: string; subtitle?: string}> = {
		// One sheet, two tabs (kitecorp/transferify-ui#3140): the title stays, and the lede under it
		// describes the tab the URL shows, so the Requested tab is introduced as file requests.
		transfers: {
			title: m.transfers.page.title,
			titleAccent: m.transfers.page.titleAccent,
			subtitle: transfersTabFor(pathname) === "requested" ? m.reminders.page.subtitle : m.transfers.page.subtitle,
		},
		contacts: {
			title: m.contacts.page.title,
			titleAccent: m.contacts.page.titleAccent,
			subtitle: m.contacts.page.subtitle,
		},
		account: {
			title: m.account.page.title,
			titleAccent: m.account.page.titleAccent,
			subtitle: m.account.page.subtitle,
		},
		// ui#496: the sponsor review queue nests under /account and opens in its OWN sheet, so it needs
		// its own name — inheriting the parent's would announce the queue as "Account".
		admin: {title: m.sponsorAdmin.seo.title},
	};
	// Memoized with [] (#154): the setters are stable useState dispatchers, so this value object never
	// needs to change identity. Rebuilding it every render punched through the <Outlet/> bailout and
	// re-rendered every ToastContext consumer (TransferList/ContactsPage/CreateFlow) on each navigation,
	// toast tick or setUser. The list hooks' error toasts (useTransfers among them) assume these setters are stable.
	const toastContextValue = useMemo(() => ({
		showErrorToast: setShowErrorMessageToast,
		showWarningToast: setShowWarningMessageToast,
		showInfoToast: setShowInfoMessageToast,
		showSuccessToast: setShowSuccessMessageToast
	}), []);
	return (
		<ToastContext.Provider value={toastContextValue}>
			<Main>
				<AppRoot>
					{/* data-app-scroller: the ONE element that actually scrolls pages (the document never
					    does) — the consent banner measures THIS box and extends its scrollable overflow
					    when a control would otherwise be stranded under the bar (ui#758, ui#1587). It
					    padded this box until ui#1587; padding came out of the page's own height. */}
					<Content data-app-scroller>
						{/* ui#96: ONE top bar for both auth states — it swaps its own pill via
						    useIsLoggedIn. Inside the scroll container (ticket #45): the chromeless
						    absolute bar overlays the top of the page and scrolls away with it. */}
						<NavBar/>
						<Banners isLoggedIn={loggedIn} suppressActivation={pathname.startsWith("/activate/")}/>
						{/* The exact HomePage (FeatureCanvas is only its historical direct-export name) owns one stable
						    sibling slot on both home and destination routes (ui#1061). React therefore preserves the
						    SAME CreateFlow instance — selected files and live options — while the second slot swaps home
						    metadata for the non-modal destination card. Its geometry is unchanged behind the card; only
						    window-wide file drops are passive while covered. */}
						{(showHomeCanvas || reply.available) && <FeatureCanvas homeVisible={showHomeCanvas} covered={onFeaturePanel || !showHomeCanvas}/>}
						{onFeaturePanel && sheetId !== null ? (
							<FeaturePanel {...sheetHeaders[sheetId]}>
								<Outlet/>
							</FeaturePanel>
						) : (
							<Outlet/>
						)}
						{/* ui#2394: the statutory § 312k cancellation control, permanently visible rather than
						    one dropdown away. Mounted on the shell so the storefront routes carry it without each
						    one mounting its own, and INSIDE the app scroller so consent/CookieBanner counts its
						    links when it decides how much room the floating consent bar must not be allowed to
						    keep. Which routes carry it is nav/siteFooterRoutes' rule, not a condition written
						    here — and it is only the STOREFRONT ones. No working screen carries it: not the
						    landing or the send/success card it hosts, not the private destinations, not the
						    Personalization sheet (ui#2681), and the viewport-locked delivery pages never did.
						    Nothing about the clearance needs to know that. The rule walks whatever controls the
						    scroller actually holds (ui#1587), so on a page with no row it simply reads that
						    page's own last control instead. */}
						{showsSiteFooter(pathname) && <SiteFooter/>}
					</Content>
				</AppRoot>
				{/* The referral link's own gift picker (ui#2020). A shell sibling like the toasts, because
				    the link can point at any public route and the sign-in it raises completes in place on
				    whichever one the reader is on. Renders nothing at all without a `?ref=` code in the URL
				    or a choice this browser already remembered. */}
				<ReferralLandingPicker attribution={referralAttribution}/>
					{/* The promo link's coupon dialog (ui#2343). A shell sibling for the picker's reasons:
					    the link can point anywhere and the guest sign-in resumes in place. Renders nothing
					    without a verified ?promo= offer. */}
					<PromoLinkDialog/>
			<Toast open={showErrorMessageToast != null}
					   onClose={() => setShowErrorMessageToast(null)}
					   message={showErrorMessageToast}
					   variant={ToastVariants.error}/>
				<Toast open={showWarningMessageToast != null}
					   onClose={() => setShowWarningMessageToast(null)}
					   message={showWarningMessageToast}
					   variant={ToastVariants.warning}/>
				<Toast open={showInfoMessageToast != null}
					   onClose={() => setShowInfoMessageToast(null)}
					   message={showInfoMessageToast}
					   variant={ToastVariants.info}/>
				<Toast open={showSuccessMessageToast != null}
					   onClose={() => setShowSuccessMessageToast(null)}
					   message={showSuccessMessageToast}
					   variant={ToastVariants.success}/>
				{useCommandPalette()}
			</Main>
		</ToastContext.Provider>
	);
}

export default function AppWithReply() {
	return <DownloadReplyProvider><App/></DownloadReplyProvider>;
}
